import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
    Mail, 
    Phone, 
    Building2, 
    Calendar,
    Clock,
    CheckCircle,
    XCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    confirmed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

export default function BookingsManagement() {
    const { getAuthHeader } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        try {
            const response = await axios.get(`${API}/bookings`, getAuthHeader());
            setBookings(response.data);
        } catch (error) {
            console.error('Error fetching bookings:', error);
            toast.error('Failed to fetch bookings');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not specified';
        return new Date(dateString).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'Not specified';
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours);
        return `${hour > 12 ? hour - 12 : hour}:${minutes} ${hour >= 12 ? 'PM' : 'AM'}`;
    };

    return (
        <div data-testid="bookings-management">
            <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-bold">Booking Requests</h1>
            </div>

            {loading ? (
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="p-6 rounded-2xl border border-border bg-background">
                            <div className="h-6 skeleton rounded w-1/3 mb-4" />
                            <div className="h-4 skeleton rounded w-1/2" />
                        </div>
                    ))}
                </div>
            ) : bookings.length > 0 ? (
                <div className="space-y-4">
                    {bookings.map((booking) => (
                        <div 
                            key={booking.id}
                            className="p-6 rounded-2xl border border-border bg-background"
                            data-testid={`booking-${booking.id}`}
                        >
                            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                        <h3 className="text-lg font-semibold">{booking.name}</h3>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[booking.status]}`}>
                                            {booking.status}
                                        </span>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-4">
                                        <a 
                                            href={`mailto:${booking.email}`}
                                            className="flex items-center gap-2 text-muted-foreground hover:text-primary"
                                        >
                                            <Mail className="w-4 h-4" />
                                            {booking.email}
                                        </a>
                                        <a 
                                            href={`tel:${booking.phone}`}
                                            className="flex items-center gap-2 text-muted-foreground hover:text-primary"
                                        >
                                            <Phone className="w-4 h-4" />
                                            {booking.phone}
                                        </a>
                                        {booking.company && (
                                            <span className="flex items-center gap-2 text-muted-foreground">
                                                <Building2 className="w-4 h-4" />
                                                {booking.company}
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex flex-wrap gap-4 p-4 rounded-xl bg-muted/50">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-4 h-4 text-primary" />
                                            <span className="text-sm">
                                                <strong>Date:</strong> {formatDate(booking.preferred_date)}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4 text-primary" />
                                            <span className="text-sm">
                                                <strong>Time:</strong> {formatTime(booking.preferred_time)}
                                            </span>
                                        </div>
                                    </div>

                                    {booking.message && (
                                        <p className="mt-4 text-sm text-muted-foreground">
                                            <strong>Message:</strong> {booking.message}
                                        </p>
                                    )}

                                    <div className="mt-4 text-xs text-muted-foreground">
                                        Requested: {new Date(booking.created_at).toLocaleString()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-20">
                    <p className="text-lg text-muted-foreground mb-4">No booking requests yet</p>
                    <p className="text-sm text-muted-foreground">
                        Booking requests from your website will appear here.
                    </p>
                </div>
            )}
        </div>
    );
}
